/**
 * CihLogoTile — square logo container. Renders the CDNImage when `logoUrl`
 * is present; otherwise shows a deterministic initials tile with a color
 * hashed from the entity name. Used in training program and organization
 * list rows where logos are optional.
 *
 * Aliases: logo tile, initials fallback, entity avatar
 */

import { CDNImage } from "~/components/ui/cdn-image";
import { cn } from "~/lib/cn";

type Props = {
  readonly name: string;
  readonly logoUrl: string | null | undefined;
  readonly size?: number;
  readonly className?: string;
};

/** Stable positive 32-bit hash. */
function hashName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (Math.imul(hash, 31) + name.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function getInitials(name: string): string {
  const words = name.split(/[\s\-_·]+/).filter(Boolean);
  const first = words[0]?.[0];
  const second = words[1]?.[0];
  if (first && second) return (first + second).toUpperCase();
  return (words[0] ? words[0].slice(0, 2) : "?").toUpperCase();
}

/** Returns an OKLCH color with deterministic hue, fixed lightness and chroma. */
function getBackgroundColor(name: string): string {
  const hue = hashName(name) % 360;
  return `oklch(0.55 0.14 ${hue})`;
}

export function CihLogoTile({ name, logoUrl, size = 56, className }: Props) {
  if (logoUrl) {
    return (
      <div
        className={cn(
          "flex items-center justify-center overflow-hidden rounded-lg bg-gray-100",
          className,
        )}
      >
        <CDNImage
          src={logoUrl}
          alt=""
          width={size}
          height={size}
          className="h-full w-full object-contain p-2"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-lg text-sm font-medium text-white",
        className,
      )}
      style={{ backgroundColor: getBackgroundColor(name) }}
      aria-hidden="true"
    >
      {getInitials(name)}
    </div>
  );
}
